import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { AuthService } from './auth.service';
import { User } from '../../shared/models';

import { LanguageStore } from '../store/language.store';
import { BehaviorSubject, Observable, Subject, firstValueFrom, map, filter, distinctUntilChanged } from 'rxjs';
import { Router } from '@angular/router';

import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';

import * as _ from 'lodash';
import { AppStateService } from './app-state.service';
import { Garage } from '../../shared/models/garage';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from 'src/config';
import { NotificationService } from 'src/app/modules/shared/services/notification.service';
import { TranslateService } from './translate.service';

// add here when we have claims for the user
export enum USER_CLAIMS {
}

export enum USER_ROLES {
    USER = 'user',
    ADMIN = 'admin'
}

@Injectable({
    providedIn: 'root'
})
export class SignInService {
    private _translateService = inject(TranslateService);
    private _notificationService = inject(NotificationService);
    private _authService = inject(AuthService);
    private _appStateService = inject(AppStateService);
    private _languageStore = inject(LanguageStore);
    private _titleService = inject(Title);
    private _router = inject(Router);
    private _msalBroadcastService = inject(MsalBroadcastService);
    private _http = inject(HttpClient);
    private _config = inject(ConfigService);

    public userClaims$: Observable<USER_CLAIMS[]>;
    public userRoles$: Observable<USER_ROLES[]>;
    public stateHasLoaded$: Observable<boolean>;
    public userHasLoaded$: Observable<boolean>;

    public user$ = this._authService.authenticationContext;

    private _userClaims$: BehaviorSubject<USER_CLAIMS[]> = new BehaviorSubject<USER_CLAIMS[]>(undefined);
    private _userRoles$: BehaviorSubject<USER_ROLES[]> = new BehaviorSubject<USER_ROLES[]>(undefined);
    private _stateHasLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _userHasLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _ngUnsubscribe$ = new Subject<void>();


    constructor() {
        this.userClaims$ = this._userClaims$.asObservable();
        this.userRoles$ = this._userRoles$.asObservable();
        this.stateHasLoaded$ = this._stateHasLoaded$.asObservable();
        this.userHasLoaded$ = this._userHasLoaded$.asObservable();
    }

    public async load() {
        this._destoryListeners();

        this._msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
            )
            .subscribe(() => {
                // this event will always fire.
                // ensure that a user exists if we have succeded a login or if we are not logged in this will also trigger
                this._load();
            });

        this._languageStore.load();

        this._authService.authenticationContext.pipe(
            distinctUntilChanged()
        ).subscribe(user => {
            if (user) {
                this._userHasLoaded$.next(true);
            } else {
                this._userHasLoaded$.next(false);
            }
        });
    }

    public userIsLoggedIn(): boolean {
        return this._authService.isOnline();
    }

    public get user(): User | undefined {
        return this._authService.user;
    }

    public get currentLanguage() {
        return this._languageStore.stateSnapshot;
    }

    public includesClaims(claims?: USER_CLAIMS[]) {
        if (!claims) {
            claims = [];
        }

        const userClaims = this._userClaims$.value || [];
        return claims.every(c => userClaims.includes(c));
    }

    public includesRoles(roles?: USER_ROLES[]) {
        if (!roles) {
            roles = [];
        }

        const userRoles = this._userRoles$.value || [];
        return roles.every(r => userRoles.includes(r));
    }

    public userHasAccess(claims: USER_CLAIMS[], roles: USER_ROLES[]) {
        const hasAccess = this.userIsLoggedIn();
        if (claims && hasAccess && roles) {
            return this.includesClaims(claims) && this.includesRoles(roles);
        } else if (claims && hasAccess) {
            return this.includesClaims(claims);
        } else if (roles && hasAccess) {
            return this.includesRoles(roles);
        }
        return hasAccess;
    }

    private _destoryListeners() {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
    }

    private async _load() {
        const userIsOnline = this.userIsLoggedIn();

        // if online, then get user networks, else route to the login-page
        if (userIsOnline) {
            this._initWhenUserLoaded();
        } else {
            this._stateHasLoaded$.next(true);
            this._authService.goToLoginPage();
        }
    }

    private async _loadGarages() {
        try {
            const garages = await firstValueFrom(this._http.get(`${this._config.mapApiBaseUrl}/v1/garages`)
                .pipe(
                    map((x: any) => x.garages),
                    map((garageDTOs: any[]) =>
                        _.chain(garageDTOs).map(garageDTO => {
                            const garage = new Garage();
                            garage.id = garageDTO.id;
                            garage.code = garageDTO.code;
                            garage.description = garageDTO.description;
                            garage.hastusRegionCode = garageDTO.hastusRegionCode;
                            garage.trafficAreaName = garageDTO.depot;
                            garage.trafficAreaId = garageDTO.depotId;
                            garage.updateGeoJson(garageDTO.geoJson);
                            return garage;
                        }).value()
                    )
                ));

            if (garages && garages.length > 0) {
                this._appStateService.updateGarages(garages);
            }
        } catch (error) {
            this._notificationService.openText(
                this._translateService.instant('Using garages from AppState'));
            console.error('Failed to fetch user\'s garages from OMS:', error);
        }
    }

    private async _initWhenUserLoaded() {
        await this._appStateService.init();
        await this._loadGarages();

        // If we want to have different roles or claims
        this._userRoles$.next([]);
        this._userClaims$.next([]);
        this._stateHasLoaded$.next(true);

        this._titleService.setTitle(`OMS Map`);
    }
    //#endregion
}
